import type { LaunchOutcome } from "../services/launcher/types";
import { ACTIVE_CHAIN } from "../config/chains";

export const APP_VERSION = "1.0.0-testnet";

export interface ReceiptJson {
  appVersion: string;
  factoryVersion: string;
  chainId: number;
  factoryAddress: string | null;
  expectedFactoryRuntimeHash: string | null;
  observedFactoryRuntimeHash: string | null;
  tokenAddress: string;
  creator: string;
  name: string;
  symbol: string;
  decimals: number;
  humanSupply: string;
  smallestUnitSupply: string;
  effectiveFee: string;
  transaction: string;
  block: string;
  timestamp: number;
  transactionStatus: string;
  verificationPassed: boolean;
  verificationChecks: Array<{ key: string; label: string; passed: boolean; expected?: string; observed?: string }>;
  verificationFailures: Array<{ key: string; label: string }>;
  offchainMetadata?: Record<string, string>;
  demonstrationMode: boolean;
  disclaimer: string;
}

/** Serialise a launch outcome into the downloadable receipt (bigints -> strings). */
export function buildReceiptJson(
  o: LaunchOutcome,
  offchainMetadata?: Record<string, string>
): ReceiptJson {
  const r = o.receipt;
  return {
    appVersion: APP_VERSION,
    factoryVersion: ACTIVE_CHAIN.factoryVersion,
    chainId: r.chainId,
    factoryAddress: r.factoryAddress,
    expectedFactoryRuntimeHash: r.expectedFactoryHash,
    observedFactoryRuntimeHash: r.observedFactoryHash,
    tokenAddress: r.tokenAddress,
    creator: r.creator,
    name: r.name,
    symbol: r.symbol,
    decimals: r.decimals,
    humanSupply: r.supplyWhole.toString(),
    smallestUnitSupply: r.supplySmallestUnits.toString(),
    effectiveFee: r.feePaid.toString(),
    transaction: r.txHash,
    block: r.blockNumber.toString(),
    timestamp: r.timestamp,
    transactionStatus: r.transactionStatus,
    verificationPassed: o.verificationPassed,
    verificationChecks: o.verification.checks,
    verificationFailures: o.verificationFailures.map((f) => ({ key: f.key, label: f.label })),
    ...(offchainMetadata && Object.keys(offchainMetadata).length
      ? { offchainMetadata }
      : {}),
    demonstrationMode: r.demonstrationMode,
    disclaimer:
      "Independent tool. Not affiliated with or endorsed by Robinhood. Creating a token on Robinhood Chain does not list it in the Robinhood app. Off-chain metadata is user-supplied and not stored in the ERC-20 contract.",
  };
}

export function receiptToBlob(o: LaunchOutcome, offchainMetadata?: Record<string, string>): Blob {
  return new Blob([JSON.stringify(buildReceiptJson(o, offchainMetadata), null, 2)], {
    type: "application/json",
  });
}
